import React from 'react';

import { useLiveQuery } from 'dexie-react-hooks';
import { useLocation, useSearch } from 'wouter';

import db, { DEXIE_IS_LOADING } from '../lib/db';
import { cn } from '../lib/utils';
import { useProfileWorkspacePresenceStatus } from '../lib/workspace_presence_store';

import { useOptionalRouteWorkspace } from '../contexts/RouteWorkspaceContext';
import useStartDirectMessage from '../hooks/useStartDirectMessage';
import { WORKSPACE_OPTIONS_SECTION } from './modals/WorkspaceOptionsModal';
import Flexicon from './shared/Flexicon';
import ProfileTag from './shared/ProfileTag';

const ProfilePopoverContent = ({ profile }) => {
  // Fetch full profile from Dexie to ensure we have all fields including role
  const routeWorkspace = useOptionalRouteWorkspace();
  const [location, navigate] = useLocation();
  const search = useSearch();
  const profileWorkspaceId = profile?.workspace_id ? String(profile.workspace_id) : null;
  const profileId = profile?.id ? String(profile.id) : null;
  const workspaceId = profileWorkspaceId || routeWorkspace?.workspaceId || null;
  const currentWorkspaceId = routeWorkspace?.workspaceId || null;
  const currentProfileId = routeWorkspace?.profileId || null;
  const shouldLoadProfile = !!profileId && !!workspaceId;
  const fullProfile = useLiveQuery(
    () => (shouldLoadProfile ? db.profiles.get([workspaceId, profileId]) : null),
    [profileId, shouldLoadProfile, workspaceId],
    shouldLoadProfile ? DEXIE_IS_LOADING : null,
  );
  const presenceStatus = useProfileWorkspacePresenceStatus(workspaceId, profileId);
  const isProfileLoading = fullProfile === DEXIE_IS_LOADING;
  const resolvedProfile = isProfileLoading ? null : fullProfile ?? profile;
  const { isStartingDirectMessage, startDirectMessage } = useStartDirectMessage(resolvedProfile?.direct_message_key);

  // Hide until loaded to prevent flash of bad styling (e.g., non-grayscale avatar for deactivated users)
  if (isProfileLoading) return null;

  // fall back to profile prop if not found in Dexie (e.g., in storybook with mock data)
  if (!resolvedProfile?.id) return null;

  const isCurrentWorkspaceProfile = !profileWorkspaceId || !currentWorkspaceId || profileWorkspaceId === currentWorkspaceId;
  const isCurrentProfile = isCurrentWorkspaceProfile && String(resolvedProfile.id) === String(currentProfileId);
  const isDeactivated = resolvedProfile.role === 'deactivated';
  const isSharedMode = routeWorkspace?.requiresProfile !== true;
  const showNametagButton = isCurrentProfile && !isDeactivated && !isSharedMode;
  const showDirectMessageButton = !isCurrentProfile && !isDeactivated && !isSharedMode && !!resolvedProfile.direct_message_key;
  const showPresenceStatus = !!workspaceId && presenceStatus !== null && !isCurrentProfile && !isDeactivated && !isSharedMode;
  const showAnyButton = showNametagButton || showDirectMessageButton;

  // The name tag editor lives in the room's settings modal, which is URL-driven (see
  // MainLayout's `modal` / `workspace-options-section` params); DMs keep it one tap away at
  // their preferences root since that modal has no section deep-link.
  const handleNametagClick = () => {
    const nextSearchParams = new URLSearchParams(search);
    if (routeWorkspace?.isDirectMessage) {
      nextSearchParams.set('modal', 'direct-message-preferences');
    } else {
      nextSearchParams.set('modal', 'workspace-options');
      nextSearchParams.set('workspace-options-section', WORKSPACE_OPTIONS_SECTION.GROUP_PROFILE);
    }
    navigate(`${location}?${nextSearchParams.toString()}`);
  };

  return (
    <div className={cn('flex w-50 flex-col items-stretch gap-5 px-3 pt-5 font-medium opacity-100', showAnyButton ? 'pb-6' : 'pb-5')}>
      <div className="flex w-full min-w-0 flex-col items-center gap-2">
        <Flexicon resource={resolvedProfile} size="size-14" />
        <div className="flex w-full min-w-0 flex-col items-center gap-0.5">
          <div className="flex w-full min-w-0 items-baseline justify-center gap-1.5">
            <span
              className={`min-w-0 truncate pb-0.5 text-base leading-4 font-semibold ${isDeactivated ? 'text-gray-400 italic' : 'text-white'}`}
            >
              {resolvedProfile.full_name}
            </span>
            {!isDeactivated && isCurrentProfile && <span className="text-xs font-medium text-gray-400">(You)</span>}
          </div>
          {!isDeactivated && resolvedProfile.tag && (
            <ProfileTag tag={resolvedProfile.tag} hex={resolvedProfile.tag_color} className="select-none" />
          )}
          {isDeactivated && <div className="text-center text-xs font-medium text-gray-400">Deactivated</div>}
          {showPresenceStatus && (
            <div className={cn('text-center text-xs font-medium', presenceStatus ? 'text-green-600' : 'text-gray-400')}>
              {presenceStatus ? 'Online' : 'Offline'}
            </div>
          )}
        </div>
      </div>
      {showNametagButton && (
        <button
          data-profile-id={resolvedProfile.id}
          onClick={handleNametagClick}
          className="cursor-pointer self-center rounded-full bg-gray-100 px-3 py-2.5 text-center text-[13px] text-black hover:bg-white"
        >
          Add name tag
        </button>
      )}
      {showDirectMessageButton && (
        <button
          type="button"
          disabled={isStartingDirectMessage}
          onClick={startDirectMessage}
          className="cursor-pointer self-center rounded-full bg-gray-100 px-3 py-2.5 text-center text-[13px] text-black hover:bg-white disabled:cursor-wait disabled:bg-gray-100/40 disabled:text-gray-500"
        >
          Message
        </button>
      )}
    </div>
  );
};

export default ProfilePopoverContent;
